"use client";

import clsx from "clsx";
import { Fragment, useCallback, useMemo, useState } from "react";

import { Typography } from "../typography/Typography";
import { BasicCheck } from "../../icons/basic";
import {
  DirectionsArrowLeft,
  DirectionsArrowRight,
} from "../../icons/directions";
import { TextLink } from "../buttons/TextLink";
import { ButtonPrimary } from "../buttons/ButtonPrimary";
import { StepperContext, StepperContextValue } from "./StepperContext";
import { baseStyles, variants } from "./Stepper.styles";

export { useStepper } from "./StepperContext";

type StepperStep = {
  label: string;
  content: React.ReactNode;
};

export interface StepperProps {
  steps: StepperStep[];
  theme?: "dark" | "light";
  footerDescription?: string;
  onSubmit: () => void;
  onBeforeStepChange?: (
    fromStep: number,
    toStep: number,
  ) => boolean | Promise<boolean>;
  isSubmitting?: boolean;
}

const Stepper = (props: StepperProps) => {
  const {
    steps,
    theme = "dark",
    footerDescription,
    onSubmit,
    onBeforeStepChange,
    isSubmitting = false,
  } = props;

  const [currentStep, setCurrentStep] = useState(0);
  const [stepValidity, setStepValidity] = useState<Record<number, boolean>>({});

  const isCurrentStepValid = stepValidity[currentStep] ?? false;

  const handleStepChange = (newStep: number) => {
    setCurrentStep(newStep);
  };

  const setStepValid = useCallback(
    (valid: boolean) => {
      setStepValidity((prev) => ({ ...prev, [currentStep]: valid }));
    },
    [currentStep],
  );

  const contextValue: StepperContextValue = {
    setStepValid,
    isStepValid: isCurrentStepValid,
    currentStep,
    totalSteps: steps.length,
    goToNextStep: () => handleStepChange(currentStep + 1),
    goToPreviousStep: () => handleStepChange(currentStep - 1),
  };

  return (
    <StepperContext.Provider value={contextValue}>
      <div
        className={clsx("flex flex-col gap-4 w-full h-full bg-base-surface")}
      >
        {steps.length > 2 && (
          <StepperHeader steps={steps} currentStep={currentStep} />
        )}
        <StepperContent content={steps[currentStep].content} />
        <StepperFooter
          informationText={footerDescription}
          stepLabels={steps.map((step) => step.label)}
          currentStep={currentStep}
          totalSteps={steps.length}
          nextStepDisabled={!isCurrentStepValid}
          onStepChange={handleStepChange}
          onSubmit={onSubmit}
          onBeforeStepChange={onBeforeStepChange}
          isSubmitting={isSubmitting}
        />
      </div>
    </StepperContext.Provider>
  );
};

interface StepperHeaderProps {
  steps: StepperStep[];
  currentStep: number;
}

const StepperHeader = (props: StepperHeaderProps) => {
  const { steps, currentStep } = props;

  const stepStatus = useMemo(() => {
    return steps.map((_, index) => {
      return index === currentStep
        ? "current"
        : index < currentStep
          ? "completed"
          : "upcoming";
    });
  }, [steps, currentStep]);

  return (
    <div className="flex flex-col gap-6 md:gap-0 sm:flex-row sm:items-start">
      {steps.map((step, index) => (
        <Fragment key={step.label}>
          <div className="flex flex-col gap-2 items-center sm:w-[200px]">
            <div className="flex items-center justify-center gap-3 w-full">
              <div className="h-px w-8 -mr-2 bg-[#A3B4D6] sm:hidden" />
              <div className={clsx(baseStyles, variants[stepStatus[index]])}>
                <Typography
                  variant={
                    index === currentStep ? "content-body-bold" : "content-body"
                  }
                >
                  {index < currentStep ? (
                    <BasicCheck className="text-[#1BB562] size-6 shrink-0" />
                  ) : (
                    index + 1
                  )}
                </Typography>
              </div>
              <div className="h-px w-8 -ml-2 bg-[#A3B4D6] sm:hidden" />
            </div>
            <Typography
              variant={
                index === currentStep ? "content-body-bold" : "content-body"
              }
            >
              {step.label}
            </Typography>
          </div>
          {index < steps.length - 1 && (
            <div className="hidden sm:block h-px flex-1 min-w-12 -mx-12 mt-5 bg-[#A3B4D6]" />
          )}
        </Fragment>
      ))}
    </div>
  );
};

const StepperContent = (props: Pick<StepperStep, "content">) => {
  const { content } = props;

  return <div className="flex flex-grow flex-col gap-4">{content}</div>;
};

interface StepperFooterProps {
  currentStep: number;
  totalSteps: number;
  stepLabels: string[];
  nextStepDisabled: boolean;
  onStepChange: (step: number) => void;
  onSubmit: () => void;
  informationText?: string;
  onBeforeStepChange?: (
    fromStep: number,
    toStep: number,
  ) => boolean | Promise<boolean>;
  isSubmitting?: boolean;
}

const StepperFooter = (props: StepperFooterProps) => {
  const {
    informationText,
    currentStep,
    totalSteps,
    stepLabels,
    nextStepDisabled,
    onStepChange,
    onSubmit,
    onBeforeStepChange,
    isSubmitting = false,
  } = props;

  const isLastStep = currentStep === totalSteps - 1;
  const nextStepLabel = isLastStep
    ? "Versturen"
    : `Ga verder naar ${stepLabels[currentStep + 1].toLowerCase()}`;

  const submitFunction = async () => {
    if (isLastStep) {
      onSubmit();
    } else {
      const canProceed = onBeforeStepChange
        ? await onBeforeStepChange(currentStep, currentStep + 1)
        : true;
      if (canProceed) {
        onStepChange(currentStep + 1);
      }
    }
  };

  return (
    <>
      <div
        className={clsx(
          "flex flex-row gap-4",
          currentStep === 0 ? "justify-end" : "justify-between",
        )}
      >
        {currentStep !== 0 && (
          <TextLink size="small" iconLeft={DirectionsArrowLeft}>
            <button
              type="button"
              onClick={() => onStepChange(currentStep - 1)}
              disabled={isSubmitting}
            >
              Vorige
            </button>
          </TextLink>
        )}
        <ButtonPrimary icon={DirectionsArrowRight} isLoading={isSubmitting}>
          <button
            type="button"
            onClick={submitFunction}
            disabled={nextStepDisabled || isSubmitting}
          >
            {isSubmitting ? "Bezig met versturen..." : nextStepLabel}
          </button>
        </ButtonPrimary>
      </div>
      {informationText && (
        <Typography
          variant="label-small"
          className="md:w-1/2 md:ml-auto text-end text-base-secondary-on-light"
        >
          {informationText}
        </Typography>
      )}
    </>
  );
};

Stepper.Header = StepperHeader;
Stepper.Content = StepperContent;
Stepper.Footer = StepperFooter;

export { Stepper };
